import { ERWAEHNUNG, enthaeltHtml, htmlZuText, mailAufteilen } from '@netcon-work/shared'
import { Paperclip } from 'lucide-react'
import { createContext, useContext, useState, type ReactNode } from 'react'
import { cn } from './cn'

/**
 * **Markdown darstellen** – für Beschreibungen und Kommentare (Phase 4, Nachtrag
 * 03.08.2026).
 *
 * Nils: *„da sollten wir noch auch Markdown oder so als Formatierer anbieten oder bisschen
 * WYSIWYG."*
 *
 * ## Warum ein eigener Renderer und keine Bibliothek
 *
 * Ein Markdown-Paket liefert HTML, und HTML aus fremdem Text darzustellen heißt, einen
 * Sanitizer daneben zu stellen und ihn aktuell zu halten – zwei Abhängigkeiten für eine
 * Handvoll Auszeichnungen. Hier entsteht **kein HTML**, sondern React-Elemente. Was nicht
 * als Element gebaut wird, kann nicht ausgeführt werden; es gibt nichts zu entschärfen.
 *
 * Derselbe Gedanke wie bei `htmlZuText` im geteilten Paket – nur andersherum.
 *
 * ## Was unterstützt wird
 *
 * Überschriften, Listen (auch nummeriert), Zitate, Codeblöcke und Code im Fließtext,
 * fett, kursiv, durchgestrichen, Links und Trennlinien. **Keine Tabellen, keine Bilder,
 * keine Fußnoten.** Wer das braucht, schreibt es im Quellsystem – Work ist kein
 * Redaktionssystem.
 *
 * ## Was nicht durch den Renderer läuft
 *
 * **HTML-Beschreibungen aus NetCon.** Dort ist ein `*` ein Sternchen aus einer E-Mail und
 * keine Auszeichnung; sie durch einen Markdown-Renderer zu schicken würde Text kursiv
 * setzen, den niemand so gemeint hat. Solche Texte gehen weiter über `htmlZuText`.
 */

/**
 * **Wer gerade liest** – für die Erwähnung, die einen selbst meint (18.08.2026).
 *
 * Ein Kontext und keine Abfrage: `Markdown` steht in Kommentaren, Beschreibungen, Vorschauen
 * und in Tests. Ein `useQuery` mittendrin verlangte überall einen Abfrage-Client; der Kontext
 * hat einen Standard (`null`) und wird dort gefüllt, wo die Sitzung ohnehin bekannt ist – in
 * der `AppShell`. Ohne Provider ist niemand hervorgehoben, und das ist der richtige Rückfall.
 */
const MeineKennung = createContext<string | null>(null)

export function MeineKennungProvider({
  personId,
  children,
}: {
  personId: string
  children: ReactNode
}) {
  return <MeineKennung.Provider value={personId}>{children}</MeineKennung.Provider>
}

/**
 * **Eine Erwähnung** – `@[Name](person:01K…)`.
 *
 * Kein Link: es gibt in Work keine Personenseite, und ein Link, der nirgendwohin führt, ist
 * eine Enttäuschung mit Unterstrich. Was die Marke leistet, ist die Unterscheidung zwischen
 * *irgendjemand ist angesprochen* und **du** bist angesprochen.
 */
function Erwaehnung({ name, personId }: { name: string; personId: string }) {
  const ich = useContext(MeineKennung)
  const meine = ich !== null && ich === personId
  return (
    <span
      data-person={personId}
      title={meine ? 'Du bist hier erwähnt' : `${name} ist hier erwähnt`}
      className={
        meine
          ? 'rounded bg-accent-soft px-1 py-px font-semibold text-accent-deep'
          : 'rounded bg-[#f3f2ef] px-1 py-px text-ink2'
      }
    >
      @{name}
    </span>
  )
}

/** Nur Ziele, die man anklicken darf. `javascript:` gehört nicht dazu. */
const ERLAUBTE_PROTOKOLLE = /^(https?:|mailto:)/i

/** Ein Link in Markdown-Schreibweise: `[Text](Ziel)`. */
const LINK = /\[([^\]]+)\]\(([^)\s]+)\)/

/**
 * **Ein Bild, das Work nicht zeigen kann** – `![alt](ziel){width=… height=…}`
 * (20.08.2026).
 *
 * Nils, mit einem Bildschirmfoto: *„Können wir GitLab Bilder auch anzeigen?"* An der
 * Stelle eines Screenshots stand
 *
 * ```
 * !image{width=648 height=128}
 * ```
 *
 * Das ist kein Text aus der Quelle, sondern das, was dieser Renderer aus
 * `![image](/uploads/…/bild.png){width=648 height=128}` übrig ließ: `LINK` griff auf den
 * Klammerausdruck, das Ziel war kein erlaubtes Protokoll, also blieb der Linktext stehen –
 * und `!` und Maßangabe standen nie zur Debatte, weil sie niemand ansah.
 *
 * **Der Regelfall ist seitdem ein anderer**: GitLab-Bilder werden beim Import in die
 * Work-Ablage geholt und stehen als `![](anhang:…)` da. Diese Regel ist für den Rest –
 * ein gelöschter Upload, ein Projekt ohne Zugang. Sie muss **vor** `LINK` stehen – sonst
 * greift der zuerst und lässt dasselbe Bruchstück zurück wie bisher.
 */
const BILD = /!\[([^\]]*)\]\(([^)\s]+)\)(?:\{[^}\n]{0,120}\})?/
/**
 * **Eine Datei aus der Work-Ablage** – `[AW__WE___Faserplanung.msg](anhang:01K…)`
 * (21.08.2026, [E-201](../../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * Nils vor einem GitLab-Kommentar, in dem genau dieser Name stand – als bloßer Text:
 * *„Kann ich hier auch auf Dateien zugreifen?"* Im Kommentar steht ein Link auf
 * `/uploads/…`, und `baueLink` verwirft jedes Ziel ohne erlaubtes Protokoll. Übrig blieb
 * der Linktext.
 *
 * Seitdem holt der Import die Datei in die Work-Ablage und setzt diesen Platzhalter an
 * ihre Stelle. Er sieht aus wie ein gewöhnlicher Link und **muss deshalb vor `LINK`
 * stehen** – sonst greift der zuerst, findet `anhang:` nicht in den erlaubten Protokollen
 * und macht wieder Text daraus.
 */
const ANHANG_DATEI = /\[([^\]\n]{1,300})\]\(anhang:([0-9A-Za-z]{26})\)/
/** Eine nackte URL im Fließtext – die schreibt niemand als Markdown-Link. */
const NACKTE_URL = /(https?:\/\/[^\s<>()]+)/
const FETT = /\*\*([^*]+)\*\*/
const KURSIV = /(?<![*\w])[*_]([^*_\n]+)[*_](?![*\w])/
const DURCHGESTRICHEN = /~~([^~]+)~~/
const CODE = /`([^`\n]+)`/

/**
 * **Ab wann eine Zeile nicht von Hand so gesetzt wurde** – gemessen an ihrer Länge.
 *
 * Wer eine Adresse schreibt, eine Grußformel oder einen Stichpunkt, bleibt kurz. Wer eine
 * Zeile mit 65 Zeichen beendet und in der nächsten weiterschreibt, hat sie nicht beendet –
 * da hat ein Werkzeug umbrochen.
 */
export const UMBRUCH_AB = 65

/** Zeilenanfänge, die etwas Eigenes beginnen – an die wird nie angehängt. */
const BLOCKANFANG = /^\s*(?:[-*+•>#|]|\d+[.)]\s)/

/**
 * **Hart umbrochenen Fließtext wieder zusammensetzen**
 * ([E-387](../../../../docs/ENTSCHEIDUNGEN.md#e-387), 01.10.2026).
 *
 * Nils vor dem Lesemodus: *„echt nicht schön"*. Der Grund stand nicht im Lesemodus, sondern
 * im Text: Ein Kommentar, der bei 95 Zeichen umbrochen wurde, ergibt in einer Spalte von
 * 62 Zeichen abwechselnd eine volle und eine halbe Zeile – „gibt dir", „ein Knopf." allein
 * auf einer Zeile. Das sieht kaputt aus, und zwar überall, nicht nur im Lesemodus.
 *
 * Woher solche Zeilen kommen: aus jedem Werkzeug, das auf eine feste Breite umbricht – aus
 * Mails im Nur-Text-Format, aus `git`-Nachrichten, und aus den Kommentaren, die Work selbst
 * über die Schnittstelle schreibt.
 *
 * **Die Länge entscheidet, nicht der Inhalt.** Eine kurze Zeile bleibt eine kurze Zeile –
 * damit bleibt die Zusage von {@link bloeckeBauen} unberührt, dass die vier Zeilen einer
 * Adresse vier Zeilen bleiben. Nur was lang genug ist, um umbrochen worden zu sein, fließt
 * wieder zusammen; die nächste Zeile darf dafür nichts Eigenes beginnen (Stichpunkt, Zitat,
 * Überschrift) und nicht eingerückt sein.
 */
export function weicheUmbrueche(zeilen: string[]): string[] {
  const raus: string[] = []
  for (const zeile of zeilen) {
    const vorige = raus[raus.length - 1]
    const fortsetzung =
      vorige !== undefined &&
      vorige.trimEnd().length >= UMBRUCH_AB &&
      zeile.trim() !== '' &&
      !/^\s/.test(zeile) &&
      !BLOCKANFANG.test(zeile)
    if (fortsetzung) raus[raus.length - 1] = `${vorige.trimEnd()} ${zeile}`
    else raus.push(zeile)
  }
  return raus
}

/**
 * Fließtext auszeichnen.
 *
 * Arbeitet sich von außen nach innen: die **erste** passende Auszeichnung im Text teilt
 * ihn in drei Stücke, und die Ränder laufen erneut durch. Das ist langsamer als ein
 * richtiger Parser und für Absätze in einem Ticket völlig ausreichend – und es kann nicht
 * in eine Endlosschleife geraten, weil jedes Teilstück kürzer ist als das Ganze.
 */
function inline(text: string, schluessel = 'i'): ReactNode[] {
  if (!text) return []

  const versuche: [RegExp, (inhalt: string, ziel: string) => ReactNode][] = [
    [
      CODE,
      (inhalt) => (
        <code className="rounded bg-[#f3f2ef] px-1 py-px font-mono text-[0.92em]">{inhalt}</code>
      ),
    ],
    [FETT, (inhalt) => <strong>{inline(inhalt, `${schluessel}f`)}</strong>],
    [DURCHGESTRICHEN, (inhalt) => <s className="text-muted">{inline(inhalt, `${schluessel}d`)}</s>],
    [KURSIV, (inhalt) => <em>{inline(inhalt, `${schluessel}k`)}</em>],
    /**
     * **Vor dem Link**, und das ist die ganze Kunst: eine Erwähnung *ist* ein Link in
     * Markdown-Schreibweise (`[Name](person:01K…)`). Stünde sie dahinter, machte `LINK`
     * daraus Klartext, und die Marke gäbe es nie. Umgekehrt ist genau das der Rückfall
     * überall dort, wo dieser Renderer nicht läuft – in einer Mail, in GitLab: „@Name".
     */
    [ERWAEHNUNG, (name, kennung) => <Erwaehnung name={name} personId={kennung} />],
    /** **Vor dem Link** – siehe `BILD`. */
    [BILD, (alt, ziel) => <FehlendesBild alt={alt} ziel={ziel} />],
    /** **Ebenfalls vor dem Link** – siehe `ANHANG_DATEI`. */
    [ANHANG_DATEI, (name, id) => <Anhangdatei name={name} id={id} />],
    [LINK, (inhalt, ziel) => baueLink(ziel, inline(inhalt, `${schluessel}l`))],
    [NACKTE_URL, (inhalt) => baueLink(inhalt, inhalt)],
  ]

  for (const [muster, bauen] of versuche) {
    const treffer = muster.exec(text)
    if (!treffer) continue
    const vorher = text.slice(0, treffer.index)
    const nachher = text.slice(treffer.index + treffer[0].length)
    return [
      ...inline(vorher, `${schluessel}v`),
      <span key={`${schluessel}-${treffer.index}`}>
        {bauen(treffer[1] ?? '', treffer[2] ?? treffer[1] ?? '')}
      </span>,
      ...inline(nachher, `${schluessel}n`),
    ]
  }

  return [text]
}

/**
 * **Ein Bild, das nur im Quellsystem liegt.**
 *
 * Steht das Ziel im Netz (`https://…`), wird daraus ein Link – dorthin kommt der
 * Entwickler, und mehr kann Work nicht zusagen. Ein Bild von einer fremden Adresse
 * *einzubetten* wäre etwas anderes: dann verriete jedes Öffnen einer Aufgabe dem fremden
 * Server, wer wann welches Ticket liest.
 *
 * Bleibt ein relativer Pfad wie `/uploads/…` übrig, steht dort ein Hinweis statt eines
 * Bruchstücks. **Nicht nichts:** ein Bild spurlos zu verschlucken ist genau der Fehler,
 * den [E-141](../../../../docs/ENTSCHEIDUNGEN.md) beschreibt – die Beschreibung sieht
 * danach vollständig aus.
 */
function FehlendesBild({ alt, ziel }: { alt: string; ziel: string }) {
  const beschriftung = alt.trim() || 'Bild'
  if (ERLAUBTE_PROTOKOLLE.test(ziel)) return baueLink(ziel, beschriftung)
  return (
    <span
      title="Dieses Bild liegt nur im Quellsystem – Work konnte es nicht übernehmen."
      className="rounded border border-dashed border-line2 px-1.5 py-px text-[0.92em] text-muted"
    >
      {beschriftung} (nur in der Quelle)
    </span>
  )
}

function baueLink(ziel: string, inhalt: ReactNode): ReactNode {
  if (!ERLAUBTE_PROTOKOLLE.test(ziel)) return <>{inhalt}</>
  return (
    <a
      href={ziel}
      target="_blank"
      rel="noreferrer"
      className="text-accent-deep underline underline-offset-2"
    >
      {inhalt}
    </a>
  )
}

/**
 * Einen Markdown-Text zu React-Elementen machen.
 *
 * `kompakt` lässt die Abstände zwischen den Blöcken schrumpfen – richtig für einen
 * Kommentar aus zwei Zeilen, falsch für eine Beschreibung mit Überschriften.
 *
 * **`dokument`** ([Z-14](../../../../docs/phasen/zusatz-14-ablage.md), 11.09.2026): Überschriften
 * bekommen echte Größen und Abstände statt nur Fettdruck. In einer Aufgabenbeschreibung ist
 * eine `#`-Zeile eine Zwischenüberschrift in einem Kasten und darf nicht schreien; in einem
 * Dokument der Ablage *ist* sie die Gliederung – Nils sah die Vorlage zur NetCon.Cloud-Anbindung
 * im Lesemodus als eine Wand aus gleich großem Text.
 */
export function Markdown({
  text,
  kompakt = false,
  dokument = false,
  allesZeigen = false,
}: {
  text: string
  kompakt?: boolean
  dokument?: boolean
  /**
   * **Signatur und älterer Verlauf stehen offen** – für den Lesemodus
   * ([E-384](../../../../docs/ENTSCHEIDUNGEN.md#e-384)). Im Verlauf einer Aufgabe sind sie
   * Ballast und werden weggeklappt; wer ausdrücklich „lesen" sagt, meint das Ganze.
   */
  allesZeigen?: boolean
}) {
  /**
   * **Anhänge zuerst** (Phase 9) – vor der Weiche zwischen HTML und Markdown.
   *
   * Der Platzhalter `![](anhang:<id>)` steht in NetCon-Beschreibungen, und die sind HTML.
   * Ginge er den HTML-Weg, machte `htmlZuText` sichtbaren Text daraus: „![](anhang:01K…)"
   * an der Stelle, an der ein Bild sein sollte. Er muss also **oberhalb** beider Wege
   * behandelt werden.
   */
  const stuecke = text.split(ANHANG_PLATZHALTER)
  if (stuecke.length > 1) {
    return (
      <>
        {stuecke.map((stueck, n) =>
          /**
           * Die Aufteilung mit einer Gruppe im Muster liefert abwechselnd Text und Kennung –
           * ungerade Positionen sind die Kennungen. Das ist der Vertrag von `String.split`
           * mit Gruppen, und er ist der Grund, warum das Muster genau **eine** hat.
           */
          n % 2 === 1 ? (
            <Anhangbild key={`a${n}`} id={stueck} />
          ) : (
            <Markdown
              key={`t${n}`}
              text={stueck}
              kompakt={kompakt}
              dokument={dokument}
              allesZeigen={allesZeigen}
            />
          ),
        )}
      </>
    )
  }

  /**
   * **HTML-Texte gehen den alten Weg.** Ein Word-Export ist kein Markdown, und ihn so zu
   * behandeln macht aus jedem Sternchen einer Aufzählung eine Kursivschrift.
   */
  const inhalt = enthaeltHtml(text) ? htmlZuText(text) : text
  const nurText = enthaeltHtml(text)

  if (nurText) {
    return <Mailtext text={inhalt} kompakt={kompakt} allesZeigen={allesZeigen} />
  }

  return <>{bloeckeBauen(inhalt, { kompakt, dokument })}</>
}

/**
 * **Ein Text aus einer Mail – gegliedert statt am Stück**
 * ([E-384](../../../../docs/ENTSCHEIDUNGEN.md#e-384), 01.10.2026).
 *
 * Bis hierher stand hier **ein** `<p>` mit `whitespace-pre-wrap`. Christian Hunhoff, 30.09.:
 * *„es handelt sich hier um einen einzigen Textblock."* Er hat damit nicht die Menge gemeint,
 * sondern die fehlende Abstufung: An `01M3RNRH…` gemessen sind von 26 Absätzen **drei Zeilen
 * die Nachricht** – der Rest ist Signatur, Haftungsausschluss und die zitierte Vormail, alles
 * in derselben Größe.
 *
 * Jetzt: echte Absätze, und was nicht die Nachricht ist, steht hinter einem Aufklapper.
 * `mailAufteilen` entscheidet, **was** was ist – und teilt im Zweifel nicht.
 *
 * **Immer noch kein HTML.** Hier entstehen React-Elemente aus Text; es gibt nichts zu
 * entschärfen. Die Begründung von `htmlZuText` gilt unverändert.
 */
function Mailtext({
  text,
  kompakt,
  allesZeigen,
}: {
  text: string
  kompakt: boolean
  allesZeigen: boolean
}) {
  const abschnitte = mailAufteilen(text)
  const inhalt = abschnitte.find((a) => a.art === 'inhalt')
  const signatur = abschnitte.find((a) => a.art === 'signatur')
  const verlauf = abschnitte.find((a) => a.art === 'verlauf')

  /** Im Lesemodus steht alles offen – sonst wäre hinter dem Knopf wieder ein Knopf. */
  if (allesZeigen) {
    return (
      <div className="break-words">
        {abschnitte.map((a) => (
          <Absaetze key={a.art} text={a.text} kompakt={kompakt} gedaempft={a.art !== 'inhalt'} />
        ))}
      </div>
    )
  }

  return (
    <div className="break-words">
      <Absaetze text={inhalt?.text ?? text} kompakt={kompakt} />
      {signatur && (
        <Aufklapper
          zu={`Signatur und Fußzeile – ${signatur.zeilen} ${signatur.zeilen === 1 ? 'Zeile' : 'Zeilen'}`}
          auf="Signatur ausblenden"
        >
          <Absaetze text={signatur.text} kompakt={kompakt} gedaempft />
        </Aufklapper>
      )}
      {verlauf && (
        <Aufklapper
          zu={`Älteren Verlauf zeigen – ${verlauf.zeilen} ${verlauf.zeilen === 1 ? 'Zeile' : 'Zeilen'}`}
          auf="Älteren Verlauf ausblenden"
        >
          <Absaetze text={verlauf.text} kompakt={kompakt} gedaempft />
        </Aufklapper>
      )}
    </div>
  )
}

/**
 * **Absätze statt eines Blocks.** Getrennt wird an der Leerzeile – das ist, was `htmlZuText` aus
 * `</p>`, `<br><br>` und Tabellenzeilen macht. Innerhalb eines Absatzes bleibt
 * `whitespace-pre-wrap`: Eine Adresse aus vier Zeilen ist ein Absatz und keine vier.
 */
function Absaetze({
  text,
  kompakt,
  gedaempft = false,
}: {
  text: string
  kompakt: boolean
  gedaempft?: boolean
}) {
  const absaetze = text
    .split(/\n{2,}/)
    .filter((a) => a.trim() !== '')
    /** Hart umbrochene Mailzeilen fließen wieder zusammen – siehe {@link weicheUmbrueche}. */
    .map((a) => weicheUmbrueche(a.split('\n')).join('\n'))
  if (absaetze.length === 0) return null
  return (
    <>
      {absaetze.map((a, n) => (
        <p
          key={n}
          className={cn(
            'break-words whitespace-pre-wrap',
            n > 0 && (kompakt ? 'mt-1.5' : 'mt-2.5'),
            gedaempft && 'text-muted',
          )}
        >
          {a}
        </p>
      ))}
    </>
  )
}

/**
 * **Der Aufklapper.** Zu, aber nie weg: Die Beschriftung sagt, **was** dahinter steht und **wie
 * viel** – „Älteren Verlauf zeigen – 14 Zeilen". Ein Aufklapper, der nur „mehr" sagt, zwingt
 * zum Nachsehen, und dann ist nichts gewonnen.
 *
 * `useState` und kein `<details>`: Die Beschriftung ändert sich beim Öffnen, und ein `summary`,
 * das im offenen Zustand noch „zeigen" sagt, liest sich falsch.
 */
function Aufklapper({ zu, auf, children }: { zu: string; auf: string; children: ReactNode }) {
  const [offen, setOffen] = useState(false)
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        aria-expanded={offen}
        className="cursor-pointer rounded-md border border-line2 px-2 py-[2px] text-[11px] text-muted hover:bg-page hover:text-ink2"
      >
        {offen ? `▾ ${auf}` : `▸ ${zu}`}
      </button>
      {offen && <div className="mt-1.5 border-l-2 border-line2 pl-2.5">{children}</div>}
    </div>
  )
}

/** Wie ein Block gesetzt wird – siehe {@link Markdown}. */
interface Setzung {
  kompakt: boolean
  dokument: boolean
}

/**
 * **Überschriftsgrößen im Dokument-Modus** – vier Stufen, darunter alles wie `####`.
 *
 * `em` statt `px`, damit die Größe an der Schrift des Trägers hängt: dasselbe Dokument in
 * einer kleineren Vorschau bleibt in sich stimmig.
 */
const UEBERSCHRIFT_STIL: Record<number, string> = {
  1: 'text-[1.55em] font-bold tracking-[-0.3px] mt-6',
  2: 'text-[1.3em] font-bold tracking-[-0.2px] mt-5 pb-1 border-b border-line',
  3: 'text-[1.12em] font-bold mt-4',
  4: 'text-[1em] font-bold mt-3',
}

/**
 * **Eine Tabellenzeile in ihre Zellen zerlegen** – `| a | b |` → `['a', 'b']`.
 *
 * Führender und schließender Strich fallen weg, ein `\|` in einer Zelle bleibt ein Strich.
 * Exportiert für die Tests.
 */
export function tabellenzellen(zeile: string): string[] {
  const inneres = zeile.trim().replace(/^\|/, '').replace(/\|$/, '')
  return inneres.split(/(?<!\\)\|/).map((z) => z.replace(/\\\|/g, '|').trim())
}

/** Die Trennzeile unter dem Tabellenkopf: `|---|:---:|---|` – mindestens ein Strich je Zelle. */
const TABELLEN_TRENNER = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/
const TABELLEN_ZEILE = /^\s*\|.*\|\s*$/

/** `![](anhang:<id>)` – die Gruppe ist die Kennung, siehe `split` oben. */
const ANHANG_PLATZHALTER = /!\[\]\(anhang:([0-9A-Za-z]{26})\)/

/**
 * **Ein Bild aus der Work-eigenen Ablage.**
 *
 * Die Adresse zeigt auf diesen Server und nicht auf GitLab oder NetCon: der Endpunkt prüft
 * die Sitzung **und** ob die Aufgabe in den Bereich des Anwenders fällt. Ein Bild, das ohne
 * Anmeldung lädt, wäre bei Screenshots aus Kundentickets die Lücke in einer Grenze, die
 * überall sonst gilt.
 *
 * `loading="lazy"`: eine Beschreibung kann ein Dutzend Bilder tragen, und beim Öffnen des
 * Aufgabendetails soll der Text sofort dastehen.
 */
function Anhangbild({ id }: { id: string }) {
  return (
    <a href={`/api/anhang/${id}`} target="_blank" rel="noreferrer" className="mt-2 block">
      <img
        src={`/api/anhang/${id}`}
        alt="Bild aus der Aufgabe"
        loading="lazy"
        className="max-h-[420px] max-w-full rounded-lg border border-line"
      />
    </a>
  )
}

/**
 * **Eine Datei zum Herunterladen.**
 *
 * Kein Bild und keine Vorschau: eine `.msg` öffnet Outlook, ein PDF der Betrachter des
 * Anwenders. Work zeigt, **dass** es die Datei gibt, wie sie heißt und wo sie liegt – der
 * Rest ist Sache des Arbeitsplatzes.
 *
 * Die Adresse zeigt wie beim Bild auf **diesen** Server: der Endpunkt prüft die Sitzung und
 * ob die Aufgabe in den Bereich des Anwenders fällt. Ein Anhang aus einem Kundenticket ohne
 * diese Prüfung wäre die Lücke in einer Grenze, die überall sonst gilt.
 *
 * `download` mit dem Namen: sonst hinge der Name allein an der Kopfzeile des Servers, und
 * ein Klick mit „Ziel speichern unter" schriebe die Kennung als Dateinamen auf die Platte.
 */
function Anhangdatei({ name, id }: { name: string; id: string }) {
  return (
    <a
      href={`/api/anhang/${id}`}
      download={name}
      title={`${name} herunterladen`}
      className="inline-flex max-w-full items-baseline gap-1 rounded border border-line bg-[#f7f6f4] px-1.5 py-px text-accent-deep hover:bg-[#f3f2ef]"
    >
      <Paperclip className="size-3 shrink-0 self-center" aria-hidden />
      <span className="truncate">{name}</span>
    </a>
  )
}

function bloeckeBauen(text: string, setzung: Setzung): ReactNode[] {
  const { kompakt, dokument } = setzung
  const zeilen = text.split('\n')
  const bloecke: ReactNode[] = []
  const abstand = kompakt ? 'mt-1.5' : 'mt-2.5'
  let i = 0

  const ersterAbstand = (n: number) => (n === 0 ? '' : abstand)

  while (i < zeilen.length) {
    const zeile = zeilen[i] ?? ''

    // ── Codeblock ──────────────────────────────────────────────────────
    if (zeile.trimStart().startsWith('```')) {
      const inhalt: string[] = []
      i++
      while (i < zeilen.length && !(zeilen[i] ?? '').trimStart().startsWith('```')) {
        inhalt.push(zeilen[i] ?? '')
        i++
      }
      i++ // die schließende Zeile
      bloecke.push(
        <pre
          key={`c${i}`}
          className={`${ersterAbstand(bloecke.length)} overflow-x-auto rounded-lg bg-[#f7f6f4] p-2.5 font-mono text-[0.92em]`}
        >
          <code>{inhalt.join('\n')}</code>
        </pre>,
      )
      continue
    }

    // ── Leerzeile ──────────────────────────────────────────────────────
    if (!zeile.trim()) {
      i++
      continue
    }

    // ── Trennlinie ─────────────────────────────────────────────────────
    if (/^\s*([-*_])\s*\1\s*\1[\s\-*_]*$/.test(zeile)) {
      bloecke.push(<hr key={`h${i}`} className={`${abstand} border-line`} />)
      i++
      continue
    }

    // ── Überschrift ────────────────────────────────────────────────────
    const ueberschrift = /^(#{1,6})\s+(.*)$/.exec(zeile)
    if (ueberschrift) {
      const stufe = (ueberschrift[1] ?? '#').length
      if (dokument) {
        const Stufe = `h${Math.min(stufe, 4)}` as 'h1' | 'h2' | 'h3' | 'h4'
        const stil = UEBERSCHRIFT_STIL[Math.min(stufe, 4)] ?? ''
        bloecke.push(
          /** Ganz oben ohne Abstand nach oben – sonst hängt der Titel in der Luft. */
          <Stufe
            key={`u${i}`}
            className={bloecke.length === 0 ? stil.replace(/\bmt-\d+\b/, '') : stil}
          >
            {inline(ueberschrift[2] ?? '', `u${i}`)}
          </Stufe>,
        )
      } else {
        bloecke.push(
          <p
            key={`u${i}`}
            className={`${ersterAbstand(bloecke.length)} font-bold ${stufe <= 2 ? 'text-[1.08em]' : ''}`}
          >
            {inline(ueberschrift[2] ?? '', `u${i}`)}
          </p>,
        )
      }
      i++
      continue
    }

    // ── Tabelle ────────────────────────────────────────────────────────
    /**
     * **GitHub-Tabellen** (11.09.2026, Z-14): Kopfzeile, Trennzeile, Zeilen – erkannt am
     * Paar aus `| … |` und `|---|`. Eine einzelne Zeile mit Strichen bleibt Text: in einem
     * Ticket steht auch mal `a | b`, und das ist keine Tabelle.
     */
    if (TABELLEN_ZEILE.test(zeile) && TABELLEN_TRENNER.test(zeilen[i + 1] ?? '')) {
      const kopf = tabellenzellen(zeile)
      const koerper: string[][] = []
      i += 2
      while (i < zeilen.length && TABELLEN_ZEILE.test(zeilen[i] ?? '')) {
        koerper.push(tabellenzellen(zeilen[i] ?? ''))
        i++
      }
      bloecke.push(
        <div key={`t${i}`} className={`${ersterAbstand(bloecke.length)} overflow-x-auto`}>
          <table className="w-full border-collapse text-[0.95em]">
            <thead>
              <tr>
                {kopf.map((z, n) => (
                  <th
                    key={n}
                    className="border-b border-line2 bg-[#f7f6f4] px-2 py-1 text-left font-semibold"
                  >
                    {inline(z, `th${i}-${n}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {koerper.map((zeile, r) => (
                <tr key={r} className="border-b border-line align-top">
                  {kopf.map((_, c) => (
                    <td key={c} className="px-2 py-1">
                      {inline(zeile[c] ?? '', `td${i}-${r}-${c}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      )
      continue
    }

    // ── Zitat ──────────────────────────────────────────────────────────
    if (/^\s*>\s?/.test(zeile)) {
      const inhalt: string[] = []
      while (i < zeilen.length && /^\s*>\s?/.test(zeilen[i] ?? '')) {
        inhalt.push((zeilen[i] ?? '').replace(/^\s*>\s?/, ''))
        i++
      }
      bloecke.push(
        <blockquote
          key={`z${i}`}
          className={`${ersterAbstand(bloecke.length)} border-l-2 border-line2 pl-2.5 text-ink2`}
        >
          {bloeckeBauen(inhalt.join('\n'), { kompakt: true, dokument })}
        </blockquote>,
      )
      continue
    }

    // ── Liste ──────────────────────────────────────────────────────────
    const punkt = /^\s*([-*+]|\d+\.)\s+/
    if (punkt.test(zeile)) {
      const nummeriert = /^\s*\d+\.\s+/.test(zeile)
      /**
       * **Eine nummerierte Liste beginnt bei ihrer ersten Zahl** (23.09.2026, von Davin
       * gemeldet: *„Eigentlich habe ich hier die beiden Punkte mit «4.» und «5.» geschrieben.
       * Beim Klick auf Speichern wird da aber irgendwie «1.» und «1.» raus"*).
       *
       * Der Text war nie falsch – in der Datenbank stand weiter „4."; nur das `<ol>` bekam kein
       * `start` und zählte deshalb immer von vorn. Wer, wie Davin, auf eine nummerierte Liste
       * **antwortet**, schreibt genau nicht bei 1 los – und stand dann vor einer Anzeige, die
       * seine Nummern stillschweigend umschrieb.
       *
       * Zwei durch eine Leerzeile getrennte Punkte sind zwei Listen; ohne `start` fingen beide
       * bei 1 an. Genau das hat er gesehen.
       */
      const ersteNummer = Number(/^\s*(\d+)\./.exec(zeile)?.[1] ?? '1')
      const punkte: string[] = []
      while (i < zeilen.length && punkt.test(zeilen[i] ?? '')) {
        let letzte = (zeilen[i] ?? '').replace(punkt, '')
        let text = letzte
        i++
        /**
         * **Ein zu lang umbrochener Stichpunkt bleibt ein Stichpunkt** (E-387). Vorher fiel
         * seine Fortsetzung aus der Liste heraus und stand als eigener Absatz darunter – „für
         * den Fall, dass es trotzdem" / „viel bleibt." Dieselbe Regel wie in
         * {@link weicheUmbrueche}: Es zählt die Länge der **zuletzt** angehängten Zeile, nicht
         * die des gewachsenen Punktes – sonst schluckte der erste lange Punkt alles, was folgt.
         */
        while (
          i < zeilen.length &&
          letzte.trimEnd().length >= UMBRUCH_AB &&
          (zeilen[i] ?? '').trim() !== '' &&
          !punkt.test(zeilen[i] ?? '') &&
          !/^\s*[>#]/.test(zeilen[i] ?? '') &&
          !(zeilen[i] ?? '').trimStart().startsWith('```')
        ) {
          letzte = (zeilen[i] ?? '').trim()
          text = `${text.trimEnd()} ${letzte}`
          i++
        }
        punkte.push(text)
      }
      const Liste = nummeriert ? 'ol' : 'ul'
      bloecke.push(
        <Liste
          key={`l${i}`}
          {...(nummeriert && Number.isFinite(ersteNummer) && ersteNummer !== 1
            ? { start: ersteNummer }
            : {})}
          className={`${ersterAbstand(bloecke.length)} ml-4 ${nummeriert ? 'list-decimal' : 'list-disc'}`}
        >
          {punkte.map((p, n) => {
            /** `- [ ] offen` / `- [x] erledigt` – ein Häkchen zum Ansehen, nicht zum Klicken. */
            const haken = /^\[([ xX])\]\s+/.exec(p)
            return (
              <li key={n} className={haken ? 'list-none -ml-4 break-words' : 'break-words'}>
                {haken && (
                  <span className="mr-1.5 inline-block w-3.5 text-center" aria-hidden>
                    {haken[1] === ' ' ? '☐' : '☑'}
                  </span>
                )}
                {inline(haken ? p.slice(haken[0].length) : p, `l${i}-${n}`)}
              </li>
            )
          })}
        </Liste>,
      )
      continue
    }

    // ── Absatz ─────────────────────────────────────────────────────────
    const absatz: string[] = []
    while (
      i < zeilen.length &&
      (zeilen[i] ?? '').trim() &&
      !punkt.test(zeilen[i] ?? '') &&
      !/^\s*>/.test(zeilen[i] ?? '') &&
      !/^#{1,6}\s/.test(zeilen[i] ?? '') &&
      !(zeilen[i] ?? '').trimStart().startsWith('```') &&
      !(TABELLEN_ZEILE.test(zeilen[i] ?? '') && TABELLEN_TRENNER.test(zeilen[i + 1] ?? ''))
    ) {
      absatz.push(zeilen[i] ?? '')
      i++
    }
    bloecke.push(
      <p key={`p${i}`} className={`${ersterAbstand(bloecke.length)} break-words`}>
        {/**
         * **Einzelne Umbrüche bleiben Umbrüche.** In striktem Markdown wäre eine Zeile
         * ohne Leerzeile dahinter Teil desselben Absatzes; in einem Ticket schreibt aber
         * niemand so, und aus einer Adresse würde eine Zeile.
         *
         * **Es sei denn, die Zeile war zum Umbrechen zu lang** – dann hat sie niemand so
         * geschrieben, sondern ein Werkzeug gesetzt ({@link weicheUmbrueche}, E-387).
         */}
        {weicheUmbrueche(absatz).flatMap((z, n) => [
          ...(n > 0 ? [<br key={`b${n}`} />] : []),
          ...inline(z, `p${i}-${n}`),
        ])}
      </p>,
    )
  }

  return bloecke
}
